import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '#/store/store';
import { IFiatDepositProperty } from '#/api/v4Service';
import { KYC_APPROVED_STATUS, TOTP_VERIFICATION_TYPE } from '#/constants/audd';
import { getUrlParams } from '#/util/urlParams';
import { IWalletOption, resolveWalletId } from '#/util/walletId';
import { initializeWidget, fetchDepositAddress, fetchWallets } from './auddThunks';

/**
 * A crypto network, backed by its own payment route. Everything that used to be shared across
 * networks — the route id fees are priced against, the PSP the deposit address comes from, the
 * address tag type, 2FA and address validation — now varies per network because each one is a
 * separate route.
 */
export interface INetworkRoute {
  name: string;
  displayName: string;
  addressTagType: string | null;
  verificationType: string | null;
  paymentRouteId: string;
  pspServiceId: string | null;
  depositEnabled: boolean;
  withdrawalEnabled: boolean;
  validateAddress: boolean;
}

/** The AUDD route that serves the fiat (bank transfer) leg. */
export interface IFiatRoute {
  paymentRouteId: string;
  pspServiceId: string | null;
  verificationType: string | null;
}

interface IAuddState {
  // Auth
  isInitialized: boolean;
  initError: string;

  // User verification
  kycStatus: string | null;
  isUsing2fa: boolean;
  hasVirtualAccount: boolean;
  allowDepositsWithoutKyc: boolean;
  feeGroupId: string;

  // Travel Rule
  isTravelRuleEnabled: boolean;
  kycFirstName: string;
  kycLastName: string;

  // Meta from fiat_deposit_properties
  fiatAccountName: string;
  fiatBsb: string;
  fiatAccountNumber: string;
  fiatReference: string;
  fiatDepositProperties: IFiatDepositProperty[];

  // Payment routes for the currency
  fiatRoute: IFiatRoute | null;
  networkRoutes: INetworkRoute[];

  // Deposit address (crypto)
  selectedNetworkRoute: INetworkRoute | null;
  depositAddress: string;
  depositTagType: string | null;
  depositTagValue: string | null;
  depositAddressLoading: boolean;
  depositAddressError: string;

  wallets: IWalletOption[];
  selectedWalletId: string;
}

const initialState: IAuddState = {
  isInitialized: false,
  initError: '',

  kycStatus: null,
  isUsing2fa: false,
  hasVirtualAccount: false,
  allowDepositsWithoutKyc: false,
  feeGroupId: '',

  isTravelRuleEnabled: false,
  kycFirstName: '',
  kycLastName: '',

  fiatAccountName: '',
  fiatBsb: '',
  fiatAccountNumber: '',
  fiatReference: '',
  fiatDepositProperties: [],

  fiatRoute: null,
  networkRoutes: [],

  selectedNetworkRoute: null,
  depositAddress: '',
  depositTagType: null,
  depositTagValue: null,
  depositAddressLoading: false,
  depositAddressError: '',

  wallets: [],
  selectedWalletId: resolveWalletId(getUrlParams().walletId),
};

const auddSlice = createSlice({
  name: 'audd',
  initialState,
  reducers: {
    selectNetwork(state, action: PayloadAction<INetworkRoute>) {
      state.selectedNetworkRoute = action.payload;
      // Reset deposit address when switching networks
      state.depositAddress = '';
      state.depositTagType = null;
      state.depositTagValue = null;
      state.depositAddressError = '';
    },
    selectWallet(state, action: PayloadAction<string>) {
      state.selectedWalletId = action.payload;
    },
    clearSelectedNetwork(state) {
      state.selectedNetworkRoute = null;
      state.depositAddress = '';
      state.depositTagType = null;
      state.depositTagValue = null;
    },
  },
  extraReducers: (builder) => {
    // initializeWidget
    builder.addCase(initializeWidget.fulfilled, (state, action) => {
      const { fiatAccountName, fiatBsb, fiatAccountNumber, fiatReference, fiatDepositProperties, fiatRoute, networkRoutes, kycStatus, isUsing2fa, feeGroupId, hasVirtualAccount, allowDepositsWithoutKyc, isTravelRuleEnabled, kycFirstName, kycLastName } = action.payload;
      state.fiatAccountName = fiatAccountName;
      state.fiatBsb = fiatBsb;
      state.fiatAccountNumber = fiatAccountNumber;
      state.fiatReference = fiatReference;
      state.fiatDepositProperties = fiatDepositProperties;
      state.fiatRoute = fiatRoute;
      state.networkRoutes = networkRoutes;
      state.kycStatus = kycStatus;
      state.isUsing2fa = isUsing2fa;
      state.feeGroupId = feeGroupId;
      state.hasVirtualAccount = hasVirtualAccount;
      state.allowDepositsWithoutKyc = allowDepositsWithoutKyc;
      state.isTravelRuleEnabled = isTravelRuleEnabled;
      state.kycFirstName = kycFirstName;
      state.kycLastName = kycLastName;
      state.isInitialized = true;
      state.initError = '';
    });
    builder.addCase(initializeWidget.rejected, (state, action) => {
      state.initError = action.payload as string;
      state.isInitialized = false;
    });

    // fetchDepositAddress
    builder.addCase(fetchDepositAddress.pending, (state) => {
      state.depositAddressLoading = true;
      state.depositAddressError = '';
    });
    builder.addCase(fetchDepositAddress.fulfilled, (state, action) => {
      state.depositAddress = action.payload.address;
      state.depositTagType = action.payload.address_tag_type;
      state.depositTagValue = action.payload.address_tag_value;
      state.depositAddressLoading = false;
    });
    builder.addCase(fetchDepositAddress.rejected, (state, action) => {
      state.depositAddressError = action.payload as string;
      state.depositAddressLoading = false;
    });

    builder.addCase(fetchWallets.fulfilled, (state, action) => {
      state.wallets = action.payload;
    });
  },
});

export const { selectNetwork, clearSelectedNetwork, selectWallet } = auddSlice.actions;
export default auddSlice.reducer;

export const selectAudd = (state: RootState) => state.audd;
export const selectIsInitialized = (state: RootState) => state.audd.isInitialized;
export const selectInitError = (state: RootState) => state.audd.initError;
export const selectNetworkRoutes = (state: RootState) => state.audd.networkRoutes;
export const selectFiatRoute = (state: RootState) => state.audd.fiatRoute;
export const selectFeeGroupId = (state: RootState) => state.audd.feeGroupId;
export const selectSelectedNetwork = (state: RootState) => state.audd.selectedNetworkRoute;
export const selectWallets = (state: RootState) => state.audd.wallets;
export const selectSelectedWalletId = (state: RootState) => state.audd.selectedWalletId;
export const selectSelectedWallet = (state: RootState) =>
  state.audd.wallets.find((wallet) => wallet.walletId === state.audd.selectedWalletId) ?? null;
export const selectSelectedWalletName = (state: RootState) =>
  state.audd.wallets.length > 1 ? selectSelectedWallet(state)?.name ?? '' : '';
export const selectDepositAddress = (state: RootState) => state.audd.depositAddress;
export const selectDepositTagType = (state: RootState) => state.audd.depositTagType;
export const selectDepositTagValue = (state: RootState) => state.audd.depositTagValue;
export const selectDepositAddressLoading = (state: RootState) => state.audd.depositAddressLoading;
export const selectDepositAddressError = (state: RootState) => state.audd.depositAddressError;
export const selectKycStatus = (state: RootState) => state.audd.kycStatus;
export const selectIsKycApproved = (state: RootState) => state.audd.kycStatus === KYC_APPROVED_STATUS;
export const selectCanDepositCrypto = (state: RootState) =>
  state.audd.allowDepositsWithoutKyc || selectIsKycApproved(state);
export const selectCanUseFiat = (state: RootState) =>
  selectIsKycApproved(state) && state.audd.hasVirtualAccount;
export const selectIsUsing2fa = (state: RootState) => state.audd.isUsing2fa;
/** Verification the fiat leg asks for — the fiat route's own setting. */
export const selectFiatVerificationType = (state: RootState) => state.audd.fiatRoute?.verificationType ?? null;
/**
 * Whether the crypto flow can be gated behind 2FA before a network is picked — only when every
 * withdrawable network demands it, mirroring cointrader's DepositWithdrawTwoFA. One network
 * requiring TOTP must not block the networks that do not; once a network is chosen,
 * CryptoWithdrawalView gates on that network's own verification type.
 */
export const selectCryptoRequiresTotp = (state: RootState) => {
  const withdrawable = state.audd.networkRoutes.filter((route) => route.withdrawalEnabled);
  return withdrawable.length > 0
    && withdrawable.every((route) => route.verificationType === TOTP_VERIFICATION_TYPE);
};
export const selectIsTravelRuleEnabled = (state: RootState) => state.audd.isTravelRuleEnabled;
export const selectKycFullName = (state: RootState) =>
  [state.audd.kycFirstName, state.audd.kycLastName].filter(Boolean).join(' ');
export const selectHasKycName = (state: RootState) =>
  !!(state.audd.kycFirstName || state.audd.kycLastName);

export const selectFiatMeta = (state: RootState) => ({
  accountName: state.audd.fiatAccountName,
  bsb: state.audd.fiatBsb,
  accountNumber: state.audd.fiatAccountNumber,
  reference: state.audd.fiatReference,
});
export const selectFiatDepositProperties = (state: RootState) => state.audd.fiatDepositProperties;
